import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { SmartLinkStatsMediaSeriesPoint } from 'fym-shared/src/smartlinks/stats';
import { TypedMemo } from '../../../common/types';
import { useChartTokens } from '../../../music24/theme/useChartTokens';
import { Segmented, SegmentedOption } from '../../../music24/components/primitives/Segmented';
import { mediaStatsMessages } from './mediaStatsMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Media panel; each part uses its own slice of it */
import styles from './SMLMediaPanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

type DayMetric = 'plays' | 'seen';

interface SMLMediaDayChartProps {
  series: SmartLinkStatsMediaSeriesPoint[];
  /** An image has no plays: the chart shows Seen only. */
  canShowPlays: boolean;
}

/**
 * The small chart of one Media block: the plays (or the times seen) per day. The stats answer has no
 * watched share per day, so the switch is Plays / Seen. Hidden under 576 px (design c2).
 */
const SMLMediaDayChartComponent: React.FC<SMLMediaDayChartProps> = ({ series, canShowPlays }) => {
  const intl = useIntl();
  const tokens = useChartTokens();
  const [metric, setMetric] = useState<DayMetric>(canShowPlays ? 'plays' : 'seen');

  const options = useMemo<ReadonlyArray<SegmentedOption<DayMetric>>>(
    () => [
      { value: 'plays', label: intl.formatMessage(mediaStatsMessages.switchPlays) },
      { value: 'seen', label: intl.formatMessage(mediaStatsMessages.switchSeen) },
    ],
    [intl]
  );
  const label = intl.formatMessage(metric === 'plays' ? mediaStatsMessages.switchPlays : mediaStatsMessages.switchSeen);

  return (
    <div className={styles.dayChart} data-testid="media-day-chart">
      <div className={styles.dayChartHead}>
        <span className={styles.numberLabel}>{intl.formatMessage(mediaStatsMessages.perDay)}</span>
        {canShowPlays && (
          <Segmented
            options={options}
            value={metric}
            onChange={setMetric}
            ariaLabel={intl.formatMessage(mediaStatsMessages.perDay)}
          />
        )}
      </div>
      <div className={styles.dayChartBody}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={series} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
            <XAxis dataKey="date" hide />
            <Tooltip
              cursor={{ fill: tokens.cursor, opacity: 0.15 }}
              contentStyle={{ background: tokens.tooltipBg, border: `1px solid ${tokens.line}`, borderRadius: 10 }}
              labelStyle={{ color: tokens.tx2 }}
              itemStyle={{ color: tokens.tooltipTx }}
            />
            <Bar dataKey={metric} name={label} fill={tokens.brandLift} radius={[2, 2, 0, 0]} maxBarSize={14} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export const SMLMediaDayChart = TypedMemo(SMLMediaDayChartComponent);
